import { t } from 'i18next';

import { PrismVolumeModel } from '../../../../../typings/api/skymap/rest/v0/.common';
import { formattedDateTime } from '../../../utils/dateUtils';
import { isDefined } from '../../../utils/variables';
import { AssetManager } from '../../asset-manager';
import { VolumeCalculationAssetTypeGuard } from './types';

export class PrismVolume {
  static fromPropertyArray(prismVolumes?: PrismVolumeModel[]): PrismVolume[] {
    if (prismVolumes && prismVolumes.length > 0) {
      return prismVolumes
        .map((properties) => new PrismVolume(properties))
        .sort((a, b) => (Number(a.createdAt) > Number(b.createdAt) ? 1 : -1));
    }

    return [];
  }

  get displayDate() {
    return formattedDateTime(this.createdAt);
  }

  get errorText() {
    switch (this.status) {
      case 'IncompatibleAssetError':
        return t('prismVolume.error.incompatibleAssetError', { ns: 'skyviewVolumeCalculation' });
      case 'IndexLimitExceededError':
        return t('prismVolume.error.indexLimitExceededError', { ns: 'skyviewVolumeCalculation' });
      case 'InvalidMeshFormatError':
        return t('prismVolume.error.invalidMeshFormatError', { ns: 'skyviewVolumeCalculation' });
      case 'NoOverlapError':
        return t('prismVolume.error.noOverlapError', { ns: 'skyviewVolumeCalculation' });
      case 'UnknownError':
        return t('prismVolume.error.unknownError', { ns: 'skyviewVolumeCalculation' });
      default:
        return '';
    }
  }

  get statusMessage() {
    switch (this.status) {
      case 'Completed':
        return t('prismVolume.status.completed', { ns: 'skyviewVolumeCalculation' });
      case 'Pending':
        return t('prismVolume.status.pending', { ns: 'skyviewVolumeCalculation' });
      case 'CalculatingVolume':
      case 'SplittingChunks':
      case 'GeneratingResult':
        return t('prismVolume.status.inProgress', { ns: 'skyviewVolumeCalculation' });
      default:
        return t('prismVolume.status.failed', { ns: 'skyviewVolumeCalculation' });
    }
  }

  id?: string;
  name?: string;
  status?: PrismVolumeModel['status'];
  cut?: number;
  cutArea?: number;
  cutPlanArea?: number;
  fill?: number;
  fillArea?: number;
  fillPlanArea?: number;
  cellWidth?: number;
  cellDepth?: number;
  prismCount?: number;
  sourceName?: string;

  createdAt?: Date;
  sourceVolumeSourceId?: string;

  constructor(properties: Partial<PrismVolumeModel>) {
    this.id = properties.id;
    this.name = properties.name;
    this.status = properties.status;
    this.cut = properties.cut;
    this.cutArea = properties.cutArea;
    this.cutPlanArea = properties.cutPlanArea;
    this.fill = properties.fill;
    this.fillArea = properties.fillArea;
    this.fillPlanArea = properties.fillPlanArea;
    this.prismCount = properties.prismCount;
    this.createdAt = isDefined(properties.createdAt) ? new Date(properties.createdAt) : undefined;
    this.sourceVolumeSourceId = properties.sourceVolumeSourceId;
  }

  isProcessing() {
    return ['Pending', 'CalculatingVolume', 'SplittingChunks', 'GeneratingResult'].includes(
      this.status!,
    );
  }

  isCompleted() {
    return this.status === 'Completed';
  }

  getSourceName() {
    if (isDefined(this.sourceName)) {
      return this.sourceName;
    }

    const foundAsset = AssetManager.instance.getAssets().find((asset) => {
      if (VolumeCalculationAssetTypeGuard.check(asset)) {
        return asset.skyMapVolumeSourceIds.includes(this.sourceVolumeSourceId!);
      }

      return false;
    });

    this.sourceName = foundAsset?.name;
    return (
      this.sourceName ??
      t('volumes.section.sourceSurfaceNotFoundFallbackName', { ns: 'skyviewAssetMenu' })
    );
  }
}
